import React, { useState } from "react";
import styles from "./RFZOOrderDetails.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { sendAnswerForRFZORequest } from "../../store/rfzoTherapy-actions";
import Button from "../UI/Buttons/Button/Button";
import ConfirmationModal from "../UI/Modals/ConfirmationModal/ConfirmationModal";
import NotificationMessage from "../UI/NotificationMessage/NotificationMessage";
import RFZOArticleModal from "../UI/Modals/RFZOArticleModal/RFZOArticleModal";
import AddRFZOArticles from "../UI/Modals/RFZOTherapyRequestModal/AddRFZOArticles/AddRFZOArticles";
import RFZOOrderTherapy from "../RFZOOrderTherapy/RFZOOrderTherapy";
import SimpleModal from "../UI/Modals/SimpleModal/SimpleModal";
import ChatBubble from "../UI/ChatBubble/ChatBubble";
import UserTherapyFiles from "../OrderProcessingComponents/UserTherapyFiles/UserTherapyFiles";
import { RFZOTherapyResponse } from "../../types/api";

type Props = {
  show: boolean;
  noBackdrop?: boolean;
  rfzoRequest: RFZOTherapyResponse;
  archived?: boolean;
};

type StepType =
  | "INITIAL"
  | "HAVE_THERAPY"
  | "NO_THERAPY_MODAL"
  | "CURRENTLY_NOT"
  | "NOT_AT_ALL";

type UserInfoProps = {
  phone: string;
  lbo: string;
  variant?: "columns" | "flat";
};

const UserInfo: React.FC<UserInfoProps> = ({
  phone,
  lbo,
  variant = "flat",
}) => {
  if (variant === "columns") {
    return (
      <div className={`${styles.Column} ${styles.LeftColumn}`}>
        <div>Tel: {phone}</div>
        <div>LBO: {lbo}</div>
      </div>
    );
  }

  return (
    <>
      <div>Tel: {phone}</div>
      <div>LBO: {lbo}</div>
    </>
  );
};

type StepComponentProps = {
  rfzoRequest: RFZOTherapyResponse;
  setStep: (step: StepType) => void;
  copyLBO: () => void;
  archived?: boolean;
};

const InitialStep: React.FC<StepComponentProps> = ({
  rfzoRequest,
  setStep,
  copyLBO,
  archived,
}) => (
  <div className={styles.RFZOTherapyRequestModal}>
    <UserInfo
      phone={rfzoRequest.userPhone}
      lbo={rfzoRequest.userLBO}
      variant="columns"
    />
    <div className={`${styles.Column} ${styles.RightColumn}`}>
      <div className={styles.RightColumnContentWrapper}>
        <div className={styles.ChatBubble}>
          <ChatBubble noteFromUserFlag>
            {rfzoRequest.noteForMarketplace ? (
              rfzoRequest.noteForMarketplace
            ) : (
              <span className={styles.NoNoteFromUser}>
                Korisnik nije uneo napomenu
              </span>
            )}
          </ChatBubble>
        </div>
        {rfzoRequest.userTherapyFiles &&
          rfzoRequest.userTherapyFiles.length > 0 && (
            <UserTherapyFiles files={rfzoRequest.userTherapyFiles} />
          )}
        {!archived && (
          <>
            <div className={styles.RFZOTherapyRequestModalMessage}>
              <Button
                style={{ fontSize: "14px", background: "#DEFFFF" }}
                buttonType="white"
                onClick={copyLBO}
              >
                Kopiraj LBO i na portalu eApoteka proveri dostupnost terapije
              </Button>
            </div>
            <div className={styles.RFZOTherapyRequestActions}>
              <Button buttonType="blue" onClick={() => setStep("HAVE_THERAPY")}>
                Korisnik ima terapiju
              </Button>
              <Button
                buttonType="white"
                onClick={() => setStep("NO_THERAPY_MODAL")}
              >
                Korisnik nema terapiju
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
);

const HaveTherapyStep: React.FC<{ rfzoRequest: RFZOTherapyResponse }> = ({
  rfzoRequest,
}) => (
  <div className={styles.RFZOTherapyRequestModal}>
    <UserInfo phone={rfzoRequest.userPhone} lbo={rfzoRequest.userLBO} />
    <AddRFZOArticles header="Trenutna terapija" />
    <div className={styles.RightColumnContentWrapper}>
      <RFZOOrderTherapy
        hasTherapy={true}
        backdropClickHandler={() => {}}
        show={true}
        rfzoId={rfzoRequest.id}
      />
      <div className={styles.RFZOTherapyRequestActions}></div>
    </div>
  </div>
);

const NoTherapyModalStep: React.FC<Pick<StepComponentProps, "setStep">> = ({
  setStep,
}) => (
  <SimpleModal
    backdropClickHandler={() => {
      setStep("INITIAL");
    }}
    className={styles.noTherapyModal}
    show={true}
  >
    <Button
      onClick={() => setStep("CURRENTLY_NOT")}
      className={styles.noTherapyModalButton}
      buttonType="blue"
    >
      Korisniku trenutno nije dospela terapija
    </Button>
    <Button
      onClick={() => setStep("NOT_AT_ALL")}
      className={styles.noTherapyModalButton}
      buttonType="blue"
    >
      Korisnik nema propisanu terapiju
    </Button>
  </SimpleModal>
);

const CurrentlyNotStep: React.FC<{ rfzoRequest: RFZOTherapyResponse }> = ({
  rfzoRequest,
}) => (
  <div className={styles.RFZOTherapyRequestModal}>
    <UserInfo phone={rfzoRequest.userPhone} lbo={rfzoRequest.userLBO} />
    <AddRFZOArticles header="Dodavanje artikala (terapija trenutno nije dostupna)" />
    <div className={styles.RightColumnContentWrapper}>
      <div className={styles.RFZOTherapyRequestModalMessage}></div>
      <RFZOOrderTherapy
        hasTherapy={false}
        backdropClickHandler={() => {}}
        show={true}
        rfzoId={rfzoRequest.id}
      />
      <div className={styles.RFZOTherapyRequestActions}></div>
    </div>
  </div>
);

const NotAtAllStep: React.FC<{ rfzoRequest: RFZOTherapyResponse }> = ({
  rfzoRequest,
}) => {
  const dispatch = useDispatch();

  return (
    <SimpleModal className={styles.noTherapyModal} show={true}>
      <div className={styles.explanation}>
        Korisnik će biti obavešten da nema propisanu terapiju i da u slučaju da
        mu je potrebna terapija, treba da poseti lekara.
      </div>
      <Button
        onClick={() => {
          dispatch<any>(
            sendAnswerForRFZORequest(rfzoRequest.id, { items: [] }),
          );
        }}
        className={styles.noTherapyModalButton}
        buttonType="blue"
      >
        Ok
      </Button>
    </SimpleModal>
  );
};

const RFZOOrderDetails = ({ rfzoRequest, archived }: Props) => {
  const [step, setStep] = useState<StepType>("INITIAL");

  const showAddArticleModal = useSelector(
    (state: RootState) => state.rfzoTherapy.showAddRFZOArticleModal,
  );

  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  const [notificationMessageVisible, setNotificationMessageVisible] =
    useState(false);

  const copyLBO = () => {
    navigator.clipboard.writeText(rfzoRequest.userLBO);
    setNotificationMessageVisible(true);
    setTimeout(() => {
      setNotificationMessageVisible(false);
    }, 5000);
  };

  const closeConfirmationModal = () => {
    setConfirmationModalVisible(false);
  };

  const renderStepContent = () => {
    switch (step) {
      case "INITIAL":
        return (
          <InitialStep
            rfzoRequest={rfzoRequest}
            setStep={setStep}
            copyLBO={copyLBO}
            archived={archived}
          />
        );
      case "HAVE_THERAPY":
        return <HaveTherapyStep rfzoRequest={rfzoRequest} />;
      case "NO_THERAPY_MODAL":
        return <NoTherapyModalStep setStep={setStep} />;
      case "CURRENTLY_NOT":
        return <CurrentlyNotStep rfzoRequest={rfzoRequest} />;
      case "NOT_AT_ALL":
        return <NotAtAllStep rfzoRequest={rfzoRequest} />;
      default:
        // Exhaustive type checking - TypeScript will error if a case is missing
        const _exhaustive: never = step;
        if (process.env.NODE_ENV === "development") {
          console.error(`Unknown RFZO step: ${step}`);
        }
        return null;
    }
  };

  return (
    <>
      {renderStepContent()}

      {/* Common modals - hoisted to parent level */}
      {confirmationModalVisible && (
        <ConfirmationModal
          show={confirmationModalVisible}
          backdropClickHandler={closeConfirmationModal}
          order={rfzoRequest}
        />
      )}

      {notificationMessageVisible && (
        <NotificationMessage>LBO kopiran!</NotificationMessage>
      )}

      {showAddArticleModal && <RFZOArticleModal show={showAddArticleModal} />}
    </>
  );
};

export default RFZOOrderDetails;
